import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  InputSignalWithTransform,
  numberAttribute,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { JobService } from '../../services/job.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { TranscriptionSegment } from '../../models/translated-segment.model';
import { Store } from '@ngrx/store';
import { selectJobById } from '../../store/jobs/jobs.selects';
import { Job } from '../../class/job.class';
import { PageDirective } from '../../directives/page.directive';
import { ChronoPipe } from '../../pipes/chrono.pipe';
import { TranscriptionVisualizerComponent } from '../../components/transcription-visualizer/transcription-visualizer.component';
import { LucideAngularModule } from 'lucide-angular';
import { TranscriptionSegmentEditorComponent } from '../../components/transcription-segment-editor/transcription-segment-editor.component';

@Component({
  selector: 'app-transcription-editor',
  imports: [
    TranscriptionVisualizerComponent,
    LucideAngularModule,
    TranscriptionSegmentEditorComponent,
  ],
  templateUrl: './transcription-editor.component.html',
  styleUrl: './transcription-editor.component.scss',
  hostDirectives: [PageDirective],
})
export class TranscriptionEditorComponent {
  readonly jobService: JobService = inject(JobService);
  readonly store: Store = inject(Store);

  readonly jobId: InputSignalWithTransform<number, unknown> = input.required<
    number,
    unknown
  >({ transform: numberAttribute });

  protected readonly transcriptionSegments: Signal<TranscriptionSegment[]> =
    toSignal(
      toObservable(this.jobId).pipe(
        switchMap((jobId: number) => this.jobService.getTranscription(jobId)),
      ),
      { initialValue: [] },
    );

  protected readonly originalAudio: Signal<Blob | undefined> = toSignal(
    toObservable(this.jobId).pipe(
      switchMap((jobId: number) => this.jobService.getOriginalAudio(jobId)),
    ),
    { initialValue: undefined },
  );

  protected readonly originalVideo: Signal<Blob | undefined> = toSignal(
    toObservable(this.jobId).pipe(
      switchMap((jobId: number) => this.jobService.getOriginalVideo(jobId)),
    ),
    { initialValue: undefined },
  );

  protected videoRef: Signal<ElementRef<HTMLVideoElement>> = viewChild.required(
    'videoPlayer',
    { read: ElementRef<HTMLVideoElement> },
  );

  readonly job: Signal<Job | undefined> = toSignal(
    toObservable(this.jobId).pipe(
      switchMap((jobId: number) => this.store.select(selectJobById(jobId))),
    ),
  );
  activeSegement: WritableSignal<TranscriptionSegment | undefined> =
    signal(undefined);

  videoUrl: Signal<string | undefined> = computed(() => {
    if (this.originalVideo()) {
      return URL.createObjectURL(this.originalVideo()!);
    }
    return undefined;
  });

  playSegment(segment: TranscriptionSegment): void {
    const video: HTMLVideoElement = this.videoRef().nativeElement;

    const onTimeUpdate = () => {
      if (video.currentTime >= segment.end) {
        video.pause();
        video.removeEventListener('timeupdate', onTimeUpdate);
      }
    };

    video.removeEventListener('timeupdate', onTimeUpdate);
    video.currentTime = segment.start;
    video.addEventListener('timeupdate', onTimeUpdate);
    video.play();
  }

  onSegmentChange(segment: TranscriptionSegment): void {
    console.log('segment change : ', segment);
  }
}
